import React, { useState, useEffect, useRef } from 'react';
import useStore from '../../../../store';
import { useTranslation } from 'react-i18next';
import './BombDefusal.css';
import '../../ui/ArcadeShared.css';

const BombDefusal = ({ socket, onClose, timeLeft }) => {
  const { t } = useTranslation();
  const store = useStore.getState();

  // --- UI STATE (Только для перерисовки) ---
  const [gameState, setGameState] = useState('loading');
  const [score, setScore] = useState(0);
  const [multiplier, setMultiplier] = useState(1);
  const [taskDisplay, setTaskDisplay] = useState(null); // Текст задачи
  const [feedback, setFeedback] = useState(null);
  const [finalData, setFinalData] = useState(null);

  // --- НОВЫЕ STATE ДЛЯ SURVIVAL MODE ---
  const [mistakes, setMistakes] = useState(0);
  const [survivalEndTime, setSurvivalEndTime] = useState(0);
  const [survivalTimeLeft, setSurvivalTimeLeft] = useState(30);

  const [cutWires, setCutWires] = useState([]);
  const [hoveredWire, setHoveredWire] = useState(null);
  const [localTimer, setLocalTimer] = useState(100);

  // --- НОВОЕ: Визуальный "шлейф" разреза ---
  // Хранит цвет провода, который должен оставаться разрезанным 0.3 сек
  // даже если раунд уже сменился.
  const [lingeringCut, setLingeringCut] = useState(null);

  // --- НОВОЕ: Стейт для взрывающегося провода ---
  const [explodedWire, setExplodedWire] = useState(null);

  // --- LOGIC REFS (Мгновенный доступ для событий) ---
  // Сюда мы пишем данные, чтобы слушатели событий видели их СРАЗУ
  const logicRef = useRef({
    taskId: null,
    cutWires: [],     // Список перерезанных
    isHolding: false, // Держит ли палец
    hoveredColor: null, // Цвет ПОСЛЕДНЕГО активного провода под пальцем
    lastCutColor: null // <--- ДОБАВИТЬ ЭТО (чтобы помнить, что резали)
  });

  // Таймер анимации (чтобы не переусложнять стейтами)
  const taskStartTime = useRef(Date.now());
  const taskDuration = useRef(3000);

  // --- 1. Инициализация и Сокеты ---
  useEffect(() => {
    if (!socket) return;
    store.ref.work.methods.hostAction('mafiaMiniGameAction', { type: 'init' });

    const handleEvent = (data) => {
      if (!data) return;

      if (data.type === 'update' || data.type === 'next') {
        const state = data.newState || data.state;

        // Обновляем визуальный стейт
        setScore(state.score);
        setMultiplier(state.multiplier);
        setTaskDisplay(state.task);
        setGameState('playing');

        // --- НОВОЕ: Обновляем survival state ---
        setMistakes(state.mistakes || 0);

        // Fix Time Synchronization - calculate relative time to account for clock skew
        if (state.survivalEndTime && state.serverTime) {
            const timeLeftMs = state.survivalEndTime - state.serverTime;
            setSurvivalEndTime(Date.now() + timeLeftMs);
        } else {
            setSurvivalEndTime(state.survivalEndTime || 0);
        }

        // Сброс раунда
        setCutWires([]);
        setHoveredWire(null);

        // ОБНОВЛЯЕМ LOGIC REF (Критично!)
        logicRef.current.taskId = state.task.id;
        logicRef.current.cutWires = [];
        logicRef.current.hoveredColor = null;
        logicRef.current.isHolding = false;

        // Таймер
        taskStartTime.current = Date.now();
        taskDuration.current = state.task.duration;
        setLocalTimer(100);

        // ЛОВИМ ОШИБКУ
        if (data.feedback) {
            triggerFeedback(data.feedback);

            // ЕСЛИ ЭТО ОШИБКА -> ВКЛЮЧАЕМ ИСКРЫ НА ПОСЛЕДНЕМ ПРОВОДЕ
            if (data.feedback.status === 'error') {
                const badWire = logicRef.current.lastCutColor;
                if (badWire) {
                    setExplodedWire(badWire);
                    // Убираем искры через 0.5 сек
                    setTimeout(() => setExplodedWire(null), 500);
                }
            }
        }
      }
      else if (data.type === 'game_over') {
        setFinalData(data);
        setGameState('finished');
        window.croco?.haptic?.('warning');
      }
    };

    socket.on('arcade:event', handleEvent);
    return () => socket.off('arcade:event', handleEvent);
  }, [socket]);

  // --- 2. Таймер (Визуальный) ---
  useEffect(() => {
    if (gameState !== 'playing') return;
    const interval = setInterval(() => {
      const elapsed = Date.now() - taskStartTime.current;
      const pct = Math.max(0, 100 - (elapsed / taskDuration.current) * 100);
      setLocalTimer(pct);
      if (pct <= 0) {
         // Простая отправка таймаута, сервер сам решит
         if (logicRef.current.taskId) {
            // Можно добавить флаг, чтобы не спамить
         }
         store.ref.work.methods.hostAction('mafiaMiniGameAction', { type: 'timeout', taskId: logicRef.current.taskId });
      }
    }, 50);
    return () => clearInterval(interval);
  }, [gameState, taskDisplay]); // Зависимость от taskDisplay сбрасывает таймер при новой задаче

  // --- 3. Таймер выживания (Survival Timer) ---
  useEffect(() => {
    if (gameState !== 'playing' || survivalEndTime === 0) return;

    const interval = setInterval(() => {
      const remaining = Math.max(0, Math.ceil((survivalEndTime - Date.now()) / 1000));
      setSurvivalTimeLeft(remaining);

      // Можно добавить дополнительную логику при окончании времени
      if (remaining <= 0) {
        // Таймер выживания истек - игра окончена
      }
    }, 100); // Обновляем каждые 100ms для плавности

    return () => clearInterval(interval);
  }, [gameState, survivalEndTime]);

  // --- 3. ГЛАВНАЯ МЕХАНИКА (HOVER & CUT) ---

  // Хелпер: найти цвет кнопки по координатам
  const getWireColor = (x, y) => {
    // elementFromPoint вернет самый верхний элемент.
    // CSS .wire-cutter-tool { pointer-events: none; } ОБЯЗАТЕЛЕН
    const el = document.elementFromPoint(x, y);
    if (!el) return null;
    const btn = el.closest('.wire-btn');
    return btn ? btn.dataset.color : null;
  };

  // A. НАЖАЛИ (Pointer Down)
  const onWireDown = (e, color) => {
    if (gameState !== 'playing') return;

    // Предотвращаем дефолт (скролл и т.д.)
    e.preventDefault();

    // ВАЖНО: Отвязываем захват, чтобы elementFromPoint работал корректно
    if (e.target.hasPointerCapture(e.pointerId)) {
        e.target.releasePointerCapture(e.pointerId);
    }

    logicRef.current.isHolding = true;
    logicRef.current.hoveredColor = color;
    setHoveredWire(color);
    window.croco?.haptic?.('selection');
  };

  // B. ДВИГАЕМ И ОТПУСКАЕМ (Global Listeners)
  useEffect(() => {
    const handleMove = (e) => {
        if (!logicRef.current.isHolding) return;

        const color = getWireColor(e.clientX, e.clientY);

        // Запоминаем текущий цвет в Ref (даже если null)
        logicRef.current.hoveredColor = color;

        // Визуал обновляем только если изменилось
        setHoveredWire(prev => {
            if (prev !== color) {
                if (color) window.croco?.haptic?.('selection');
                return color;
            }
            return prev;
        });
    };

    const handleUp = () => {
        if (!logicRef.current.isHolding) return;
        logicRef.current.isHolding = false;

        // 1. Читаем из Ref: над каким цветом мы были в последний момент?
        const colorToCut = logicRef.current.hoveredColor;
        const { taskId, cutWires } = logicRef.current;

        // 2. Проверяем валидность
        if (colorToCut && taskId && !cutWires.includes(colorToCut)) {
            // 3. РЕЖЕМ!
            performCut(colorToCut, taskId);
        }

        // 4. Сброс визуала
        setHoveredWire(null);
        logicRef.current.hoveredColor = null;
    };

    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp);
    // Для мобильных иногда нужен touchend, если pointerup не всплыл
    window.addEventListener('touchend', handleUp);

    return () => {
        window.removeEventListener('pointermove', handleMove);
        window.removeEventListener('pointerup', handleUp);
        window.removeEventListener('touchend', handleUp);
    };
  }, []); // Пустой массив! Логика полностью на Ref, замыкания не страшны.

  const performCut = (color, taskId) => {
    // 1. Запоминаем для будущей ошибки
    logicRef.current.lastCutColor = color; // <--- НОВОЕ

    // 2. Отправляем на сервер
    store.ref.work.methods.hostAction('mafiaMiniGameAction', {
        type: 'cut',
        color,
        taskId
    });

    // 3. Логика
    setCutWires(prev => [...prev, color]);
    logicRef.current.cutWires.push(color);

    // 4. Визуал (показываем чистый разрез)
    setLingeringCut(color);
    setTimeout(() => {
        setLingeringCut(null);
    }, 500);

    // Звук "Чик" (просто резка)
    window.croco?.haptic?.('impactMedium');
  };

  const triggerFeedback = (fb) => {
    if (fb.status === 'error') {
      setFeedback('error');
      window.croco?.haptic?.('error');
      setTimeout(() => setFeedback(null), 300);
    }
  };


  // --- RENDER ---
  if (finalData) {
      const isExploded = finalData.reason === 'exploded';
      const isSurvived = !isExploded;
      const score = finalData.my?.score || 0;
      const maxStreak = finalData.my?.maxStreak || 0;
      const mistakes = finalData.my?.mistakes || 0;
      const maxMistakes = finalData.my?.maxMistakes || 0;
      return (
        <div className="mafia-overlay-bg">
            <div className="bomb-result-modal">
                <div className="result-header"><h2>{t('arcade.quiz_report')}</h2></div>
                <div className="result-stats">
                    <div className="res-row"><span>{t('arcade.bomb_score')}</span><span className="res-val">{score}</span></div>
                    <div className="res-row"><span>{t('arcade.bomb_mistakes', 'MISTAKES')}</span><span className="res-val">{mistakes} / {maxMistakes}</span></div>
                    <div className="res-row"><span>{t('arcade.bomb_streak')}</span><span className="res-val">{maxStreak}</span></div>
                </div>
                <div className={`result-stamp ${isSurvived ? 'stamp-survived' : 'stamp-exploded'}`}>{isSurvived ? t('arcade.bomb_defused', 'DEFUSED') : t('arcade.bomb_exploded', 'EXPLODED')}</div>
                <button className="result-btn" onClick={onClose}>{t('arcade.close_dossier')}</button>
            </div>
        </div>
      );
  }

  // Fallback, если задача еще не загрузилась
  const display = taskDisplay?.display || {};

  return (
    <div className={`bomb-container ${feedback === 'error' ? 'shake-anim' : ''}`}>

       {/* Статы */}
       <div className="bomb-stats-bar">
          <div className="b-stat-group"><span className="b-label">{t('arcade.bomb_score')}</span><span className="b-value">{score}</span></div>
          <div className="b-stat-group"><span className="b-label">TIMER</span><span className={`b-value survival-timer ${survivalTimeLeft < 10 ? 'danger' : ''}`}>{Math.floor(survivalTimeLeft / 60)}:{(survivalTimeLeft % 60).toString().padStart(2,'0')}</span></div>
          <div className="b-stat-group">
            <span className="b-label">STRIKES</span>
            <div className="strikes-container">
              {Array.from({length: 3}).map((_, i) => (
                <span key={i} className={`strike-icon ${i < mistakes ? '' : 'inactive'}`}>✖</span>
              ))}
            </div>
          </div>
       </div>

       {/* Экран */}
       <div className="bomb-screen">
          <div className="task-wrapper">
             <div className={`task-main-text text-${display.textColor || 'white'}`}>
                {display.mainText || t(display.textKey || '...', display.args)}
             </div>
          </div>
          <div className="timer-bar-container"><div className="timer-bar-fill" style={{ width: `${localTimer}%` }} /></div>
          {feedback === 'error' && <div className="boom-overlay"><div className="boom-text">{t('arcade.bomb_detonated')}</div></div>}
       </div>

       {/* Провода */}
       <div className="wires-grid">
          {['red', 'blue', 'green', 'yellow'].map(color => {
             const isLogicallyCut = cutWires.includes(color);
             const isLingering = lingeringCut === color;
             const isVisualCut = isLogicallyCut || isLingering;

             // --- НОВОЕ: Провод взрывается сейчас? ---
             const isExploding = explodedWire === color;

             // Показываем кусачки, только если провод еще цел (визуально) и мы над ним
             const isHovered = (hoveredWire === color) && !isVisualCut;

             // Поднимаем кнопку, если мы над ней, или она разрезана недавно, или ВЗРЫВАЕТСЯ
             const activeZIndex = (isHovered || isLingering || isExploding) ? 100 : 1;

             return (
                <div
                    key={color}
                    className={`wire-btn btn-${color} ${isVisualCut ? 'wire-is-cut' : ''}`}
                    data-color={color}
                    onPointerDown={(e) => onWireDown(e, color)}
                    style={{
                        touchAction: 'none',
                        zIndex: activeZIndex, // <--- ВАЖНО: Кнопка поверх остальных
                        position: 'relative' // Нужно для z-index
                    }}
                >
                    <div className="wire-visual">
                        {isVisualCut && (
                            <div className="cut-gap-visual">
                                <div className="copper-bit left"></div>
                                <div className="copper-bit right"></div>

                                {/* --- БЛОК ЭФФЕКТОВ (ТЕПЕРЬ ЗАВИСИТ ОТ isExploding) --- */}
                                {/* Показываем ТОЛЬКО если сервер сказал "Ошибка!" */}
                                {isExploding && (
                                    <div className="fx-container">
                                        <div className="fx-smoke"></div>
                                        <div className="fx-spark s1"></div>
                                        <div className="fx-spark s2"></div>
                                        <div className="fx-spark s3"></div>
                                        <div className="fx-spark s4"></div>
                                    </div>
                                )}
                                {/* -------------------------------------------------- */}

                            </div>
                        )}
                    </div>

                    {/* Кусачки исчезнут, потому что isHovered зависит от isVisualCut */}
                    <div className={`wire-cutter-tool ${isHovered ? 'visible' : ''}`}>
                        <div className="cutter-jaws"></div>
                        <div className="cutter-handles"></div>
                    </div>
                </div>
             );
          })}
       </div>
    </div>
  );
};

export default BombDefusal;